import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { validateBetaKey } from '../../DatabaseAPI';
import { getStoredInviteKey, hasInviteLinkError, clearStoredInvite } from '../../auth/inviteKey';
import { captureAdmissionTokenFromValidation, clearAdmissionToken } from '../../auth/betaAdmissionToken';
import './BetaKeyGate.css';

/**
 * BetaKeyGate — the closed-beta check that fronts Clerk sign-up.
 *
 * Mythic is in closed beta, so "Sign Up" must prove the visitor holds an invite
 * BEFORE handing them to Clerk. Without this gate, anyone could create a Clerk
 * identity, and would only discover they weren't admitted after completing signup —
 * leaving an orphaned Clerk user with no Django account behind it.
 *
 * ============================================================================
 * THIS GATE DOES NOT CONSUME THE KEY — that is the whole reason it is safe
 * ============================================================================
 *
 * `validateBetaKey()` -> `POST auth/validate-beta-key/` is a READ-ONLY lookup:
 * `BetaKeyValidationView` returns the key's metadata and never calls
 * `BetaKey.use_key()`. So checking a key here does NOT decrement a shared key's
 * limited `usage_count`.
 *
 * The key is actually spent later, server-side and under lock, by the Clerk
 * bridge's beta admission (`lock_and_validate_beta_key()`). This component must
 * therefore never be treated as "admission granted" — it is a courtesy pre-check
 * that stops a doomed signup early. The backend remains authoritative and will
 * re-validate at the bridge; a key that passes here can still be legitimately
 * rejected there (e.g. it was exhausted by someone else in between).
 *
 * ============================================================================
 * THE KEY IS NEVER WRITTEN TO STORAGE BY THIS COMPONENT
 * ============================================================================
 *
 * A manually-typed key stays in local component state and is handed to the caller
 * in memory only. Per the storage rules, only a URL-derived SHARED (`GRUP_`)
 * invite may live in `sessionStorage`, and `inviteKey.js` already owns that; this
 * component only ever READS it to prefill the field.
 *
 * What this component DOES persist is the backend's **admission token** from the
 * same validate response — a single-use, ~10-minute, server-invalidated carrier
 * that is useless without a completed Clerk session. That is a materially weaker
 * artifact than the key and is what lets a Google/OAuth signup survive the
 * redirect without a second prompt. The reasoning and the storage discipline live
 * in `betaAdmissionToken.js`; nothing here writes the key itself.
 */
function BetaKeyGate({ onValidated, isBusy = false }) {
    if (typeof onValidated !== 'function') {
        throw new Error(
            `BetaKeyGate requires an onValidated function; received ${typeof onValidated}.`
        );
    }

    // Prefill from a QR/shared invite when this tab captured one. A value that
    // came from the link is then AUTO-VALIDATED once (see the effect below) so a
    // scanner is not asked to press Continue on a key they cannot even read off
    // the printed card. That is safe only because validation is read-only and
    // does not consume the key — the full reasoning is on that effect.
    const [betaKey, setBetaKey] = useState(() => getStoredInviteKey() || '');
    const [inviteLinkFailed] = useState(() => hasInviteLinkError());
    // Whether the CURRENT field value still came from the invite link. Editing it
    // clears this, because a hand-typed key is a manual entry and gets manual
    // rules (see the SHARED gate below).
    const [valueIsFromInviteLink, setValueIsFromInviteLink] = useState(
        () => Boolean(getStoredInviteKey())
    );

    const [error, setError] = useState('');
    const [isValidating, setIsValidating] = useState(false);

    const trimmedKey = betaKey.trim();
    const canSubmit = trimmedKey.length > 0 && !isValidating && !isBusy;

    const handleChange = (event) => {
        setBetaKey(event.target.value);
        setValueIsFromInviteLink(false);
        setError('');
    };

    /**
     * The ONE validation implementation, shared by manual submit and the
     * URL-invite auto path.
     *
     * Deliberately not duplicated: the 429 branch, the "preserve the backend's
     * reason" branch, and the SHARED gate below are all subtle and must never
     * drift between the two entry points.
     *
     * @param keyToValidate trimmed key string
     * @param fromInviteLink whether this value came from the URL, which is what
     *   subjects it to the SHARED-only rule
     */
    const runValidation = useCallback(async (keyToValidate, { fromInviteLink }) => {
        setError('');
        setIsValidating(true);
        // Any token from a previous attempt in this tab is now stale — the user is
        // prechecking a key again, so whatever carrier the last precheck minted must
        // not survive to attach itself to this one.
        clearAdmissionToken();

        try {
            // The key itself is never logged — these are live, revocable codes and
            // console output leaks into screen shares and session replay.
            console.log('BetaKeyGate: Checking beta key before Clerk sign-up');
            const response = await validateBetaKey(keyToValidate);

            if (!response.valid) {
                // 200-with-valid:false. Surface the backend's own wording.
                console.log('BetaKeyGate: Beta key rejected by backend');
                setError(response.message || 'Invalid beta key');
                return;
            }

            /*
             * SHARED-ONLY GATE FOR URL INVITES — a security boundary, not a format
             * nicety.
             *
             * An `INDIVIDUAL` key is a bearer credential bound to one person's
             * `target_email`, and that binding is NOT enforced at redemption. So a
             * leaked individual link must not be auto-usable by whoever opened it.
             *
             * THIS IS NOW THE ONLY SHARED-ONLY ENFORCEMENT POINT. `inviteKey.js`
             * used to pre-filter on a `GRUP_` prefix, but that rejected real
             * shared keys (which carry no prefix) and broke the QR code entirely.
             * `key_type` is the real model field and the only trustworthy signal;
             * a prefix is an admin-editable naming convention.
             *
             * Manual entry is deliberately exempt: typing your own individual key
             * into this form is the intended use of an individual key.
             */
            const keyType = response.betaKey?.key_type;
            if (fromInviteLink && keyType !== 'SHARED') {
                console.error(
                    'BetaKeyGate: Invite link carried a non-shared key ' +
                    `(key_type=${keyType}); refusing to use it.`
                );
                setBetaKey('');
                setValueIsFromInviteLink(false);
                // Drop the stored invite too, or every refresh re-runs the auto
                // validation and re-earns the same rejection forever.
                clearStoredInvite();
                setError(
                    'This invite link cannot be used. Invite links only work with ' +
                    'shared event keys. If you received a personal beta key, please ' +
                    'enter it below.'
                );
                return;
            }

            /*
             * CAPTURE THE ADMISSION TOKEN BEFORE HANDING OFF.
             *
             * This is the only moment the plaintext token exists — the backend
             * returns it exactly once, in this response body, and never echoes it
             * again. Storing it here is what lets the bridge admit an OAuth signup
             * without a second key prompt.
             *
             * A capture failure MUST NOT block the signup. The token is an
             * optimization layered over an authoritative gate that still works
             * without it: no token simply means `ClerkBetaAdmissionScreen` asks for
             * the key after Clerk completes, exactly as it did before. Reporting and
             * continuing is therefore correct here — the loud failure lives in
             * `storeAdmissionToken`, where a contract drift surfaces in development.
             *
             * The error message never contains the token; `betaAdmissionToken.js`
             * reports only shape/parse problems.
             */
            try {
                captureAdmissionTokenFromValidation(response);
            } catch (tokenError) {
                console.error(
                    'BetaKeyGate: could not store the admission token; continuing without it. ' +
                    `The beta key prompt will appear once more after sign-up. ${tokenError.message}`
                );
            }

            console.log('BetaKeyGate: Beta key accepted, continuing to Clerk sign-up');
            // Hand the key back IN MEMORY so the caller can prefill Clerk-bridge
            // admission and spare the user a second entry. Not persisted here.
            onValidated(keyToValidate);
        } catch (caughtError) {
            console.error('BetaKeyGate: Beta key validation error:', caughtError);

            const message = caughtError.message || '';

            /*
             * THROTTLING GETS ITS OWN MESSAGE — `beta_key_validate` is scoped by
             * CLIENT IP, not by user. So a shared connection (office NAT, conference
             * wifi, mobile carrier, a launch event where everyone scans the same QR
             * code) burns one shared budget, and typos count against it too.
             *
             * DRF answers 429 with `{"detail": ...}` and no `message` field, so
             * `validateBetaKey()` rethrows the generic `HTTP 429: ...` string. That
             * matched the transport-failure branch below and told the user "please
             * try again" — advice that may not work for the rest of the window and
             * that invites hammering the endpoint they are already throttled on.
             *
             * THE COPY NAMES NO DURATION, DELIBERATELY. The window is owned by the
             * backend setting `BETA_KEY_VALIDATE_THROTTLE_RATE` (1000/hour prod,
             * 10000/hour under DEBUG), which is env-overridable and can be raised
             * for an event without any frontend change. Any hardcoded "wait N
             * minutes" here goes stale the moment ops turns that dial, and would be
             * telling the user something the server never promised. Section 4T.6 of
             * the login-splash manual plan verifies this message stays duration-free.
             */
            if (/^HTTP 429:/.test(message)) {
                console.warn('BetaKeyGate: beta-key validation is rate limited (429)');
                setError(
                    'Too many beta key attempts from your network. Please try again ' +
                    'later, or contact support if you need access sooner.'
                );
                return;
            }

            /*
             * PRESERVE THE BACKEND'S REASON. A rejected key is a 400 whose body
             * carries user-actionable text ("This beta key has already been used"),
             * and `validateBetaKey()` rethrows it as `error.message`. Flattening
             * that to a generic "try again" tells an exhausted-key user to retry
             * something that can never succeed, and hides the one fact that would
             * send them to request a new key. Only real transport failures get the
             * generic message.
             */
            const isTransportFailure = (
                caughtError instanceof TypeError ||
                /^HTTP \d{3}:/.test(message)
            );

            setError(
                isTransportFailure || !message
                    ? 'Could not reach the server to check that key. Please try again.'
                    : message
            );
        } finally {
            setIsValidating(false);
        }
    }, [onValidated]);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!canSubmit) return;
        runValidation(trimmedKey, { fromInviteLink: valueIsFromInviteLink });
    };

    /*
     * ========================================================================
     * AUTO-VALIDATE A URL INVITE, EXACTLY ONCE
     * ========================================================================
     *
     * A user who scanned the QR code has already presented their key. Making
     * them press Continue on a box that is already filled in is a pointless
     * step, and the printed card carries no key for them to check it against.
     * This restores the behaviour the pre-Clerk `BetaKeyValidation` screen had.
     *
     * SAFE ONLY BECAUSE VALIDATION IS READ-ONLY. `validateBetaKey()` ->
     * `POST auth/validate-beta-key/` looks the key up and returns its metadata;
     * `BetaKeyValidationView` NEVER calls `BetaKey.use_key()`, so this cannot
     * burn a seat on a limited shared key. A crawler hitting the invite URL
     * costs a lookup, not a redemption. The key is still spent exactly once,
     * server-side under lock, at the Clerk bridge.
     *
     * IF THAT ENDPOINT EVER STARTS CONSUMING, THIS EFFECT MUST BE DELETED.
     *
     * The ref guard is load-bearing, not defensive decoration: React
     * StrictMode double-invokes effects in development, and `beta_key_validate`
     * is throttled by CLIENT IP. At an event where a crowd shares one NAT,
     * every duplicated request burns a shared budget for everyone.
     *
     * Runs once per mount and never retries: a rejected key that re-fired on
     * every render would hammer a throttled endpoint and lock the user out of
     * the manual form.
     */
    const autoValidationStarted = useRef(false);
    useEffect(() => {
        if (autoValidationStarted.current) return;
        // Only a value that came FROM THE LINK is auto-submitted. A hand-typed
        // key is always an explicit user action.
        if (!valueIsFromInviteLink) return;

        const storedInvite = getStoredInviteKey();
        if (!storedInvite || !storedInvite.trim()) return;

        autoValidationStarted.current = true;
        console.log('BetaKeyGate: Auto-validating beta key from invite link');
        runValidation(storedInvite.trim(), { fromInviteLink: true });
    }, [valueIsFromInviteLink, runValidation]);

    return (
        <form className="beta-gate" onSubmit={handleSubmit}>
            {/*
              * No explanatory copy and no visible field label: the popup's own
              * "Enter Your Beta Key" heading already says what this is, and
              * repeating it as a paragraph plus a label was three statements of the
              * same instruction.
              *
              * The two message blocks below are NOT static copy and must stay —
              * they render only on failure and are the user's only feedback about
              * why a key was refused. Removing them would leave a rejected key
              * looking like a dead button.
              */}
            {/*
              * INVITE-LINK FAILURE COPY — must not demand an unobtainable value.
              *
              * The previous wording ended "Please enter it below", which was an
              * impossible instruction: the printed QR card does NOT carry the
              * beta key anywhere, so a user whose link failed to parse has no
              * way to supply one. That made a bad scan a dead end.
              *
              * The copy now tells them what to do instead — re-scan, or use the
              * request/support path. Keep it free of any instruction to type a
              * key the product never showed them.
              */}
            {inviteLinkFailed && (
                <p className="beta-gate__notice" role="status">
                    We couldn&apos;t read the invite link you used. Try scanning the code
                    again, or use the beta key from your invite email if you have one.
                </p>
            )}

            {error && (
                <p className="beta-gate__error" role="alert">
                    {error}
                </p>
            )}

            {/*
              * `aria-label` replaces the removed visible label. The input still
              * needs an accessible name — an unlabelled field is announced only as
              * "edit text" by a screen reader, which is unusable. The modal heading
              * is a separate element and does not name this control.
              */}
            <input
                id="beta-gate-key"
                className="beta-gate__input"
                type="text"
                aria-label="Beta key"
                value={betaKey}
                onChange={handleChange}
                autoComplete="off"
                spellCheck="false"
                disabled={isValidating || isBusy}
            />

            <button
                className="beta-gate__submit"
                type="submit"
                disabled={!canSubmit}
            >
                {isValidating ? 'Checking…' : 'Continue'}
            </button>
        </form>
    );
}

BetaKeyGate.propTypes = {
    /**
     * Called with the validated key (a string) once the backend accepts it.
     * The caller owns what happens next — typically opening Clerk sign-up.
     */
    onValidated: PropTypes.func.isRequired,

    /** Disables the form while the caller is mid-transition. */
    isBusy: PropTypes.bool
};

export default BetaKeyGate;
